import React from 'react'
import { css } from '@emotion/core'
import { Paragraph, Separator, Row, useContainerWidth, useStatusOverlay, useOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { ContentAdd, ContentCheck, MediaPlay } from '@fs/zion-icon'
import { useUser } from '@fs/zion-user'
import { trackLink } from '@fs/zion-analytics'
import { useHistory } from '@fs/zion-router'
import { useTimeTravel } from '@fs/zion-rootstech'
import CardListItem from '../CardListItem'
import Timestamp from '../../Timestamp'
import { AreYouSureOverlay } from '../../overlays'

import { getRelativePath, getCustomImage, getIsPastBroadcast } from '../../helpers'
import { PillButton, SocialShareButton } from '../../buttons'
import {
  InPersonBadge,
  InPersonOnlyBadge,
  LiveBadge,
  OnlineBadge,
  SessionDifficultyBadge,
  ReplayBadge,
} from '../../Badge'
import { useFlagsContext, useLoggedOutOverlay, useMyScheduleContext, useScheduleContext } from '../../../providers'
import { mapToMyScheduleDate, mapToMyScheduleHour } from '../../../components/schedule/helpers/scheduleHelpers'
import Classroom from '../../Classroom'
import { FLAGS } from '../../../config'

const scheduleMetadataCss = css`
  & > *:empty {
    display: none;
  }

  & > * > * + *::before {
    content: ' • ';
  }
`

const Badges = ({ location, difficulty, isLive, replay }) => {
  return (
    <Row gutters="nano" wrap>
      {isLive && <LiveBadge />}
      {!isLive && replay && <ReplayBadge />}
      {location?.includes('Online') && <OnlineBadge />}
      {location?.includes('In Person') && <InPersonBadge />}
      {difficulty && <SessionDifficultyBadge sessionDifficulty={difficulty} />}
    </Row>
  )
}

const Actions = ({ data, calendarDate, linkPage, asIconButton, showWatchButton }) => {
  const { [FLAGS.REMOVE_SESSION_FROM_SCHEDULE_CONFIRM]: removeConfirmFlag } = useFlagsContext()
  const [t] = useTranslation()
  const areYouSureOverlay = useOverlay()
  const { contentId, title, url, description, inMySchedule } = data
  const thumbnail = getCustomImage(data)
  const user = useUser()
  const history = useHistory()
  const { addToMySchedule, removeFromMySchedule } = useMyScheduleContext()
  const { day } = useScheduleContext()
  const showStatusOverlay = useStatusOverlay()
  const { overlay: loggedOutOverlay } = useLoggedOutOverlay()
  const sessionDate = new Date(calendarDate)
  const date = mapToMyScheduleDate(sessionDate)
  const time = mapToMyScheduleHour(sessionDate)
  const value = {
    calendarDate,
    contentId,
    date,
    time,
  }
  const showAddedOverlay = () => {
    showStatusOverlay({
      message: t('schedule.added.my-schedule.alert', 'Added to My Schedule'),
      type: 'success',
      actionLabel: t('schedule.view.my-schedule.alert', 'View Now'),
      actionCallback: () => history.push('/user/schedule'),
      transient: true,
    })
  }
  const showErrorOverlay = () => {
    showStatusOverlay({
      message: t(
        'schedule.error.my-schedule.alert',
        'You may choose up to four sessions for each time slot. Please select your top four sessions.'
      ),
      type: 'error',
      dismissable: true,
    })
  }

  const onClick = (e) => {
    const loggedIn = user?.signedIn ?? false
    if (!loggedIn) {
      trackLink({ name: 'MySchedule: logged out', action: 'click_action' })
      loggedOutOverlay?.handleClick?.(e)
    } else if (day) {
      if (inMySchedule) {
        if (removeConfirmFlag?.treatment === 'on') {
          areYouSureOverlay.open()
        } else {
          trackLink({ name: `Remove from MySchedule: ${title}`, action: 'click_action' })
          removeFromMySchedule(value)
        }
      } else {
        trackLink({ name: `Add to MySchedule: ${title}`, action: 'click_action' })
        addToMySchedule(value).then(showAddedOverlay).catch(showErrorOverlay)
      }
    }
  }

  return (
    <Row alignY="middle">
      <Row growX>
        {showWatchButton && (
          <PillButton Icon={MediaPlay} emphasis="medium" to={url}>
            {t('schedule.watch.label', 'Watch')}
          </PillButton>
        )}
        <PillButton
          Icon={inMySchedule ? ContentCheck : ContentAdd}
          emphasis={inMySchedule ? 'high' : 'medium'}
          onClick={onClick}
          color={inMySchedule ? 'blue' : undefined}
          data-testid={inMySchedule ? 'my-schedule-button-remove' : 'my-schedule-button-add'}
        >
          {t('schedule.schedule.label', 'Schedule')}
        </PillButton>
      </Row>
      <SocialShareButton
        title={title}
        linkPage={linkPage}
        asIconButton={asIconButton}
        url={url}
        thumbnail={thumbnail}
        description={description}
        size={asIconButton ? 'md' : 'lg'}
      />
      {(areYouSureOverlay.isOpen || areYouSureOverlay.transitioning) && (
        <AreYouSureOverlay
          {...areYouSureOverlay}
          instructions={t(
            'schedule.remove.my-schedule.confirmation',
            'You are about to remove this session from your personalized My Schedule? This action cannot be undone.'
          )}
          onSubmit={() => {
            trackLink({ name: `Remove from MySchedule: ${title}`, action: 'click_action' })
            removeFromMySchedule(value)
          }}
          submitButtonText={t('schedule.remove.label', 'Remove Session')}
        />
      )}
    </Row>
  )
}

/**
 * @typedef {object} ScheduleCardProps
 * @property {any} data
 * @property {number | string | null} [calendarDate]
 * @property {any} [linkPage]
 * @property {any} [dataForVideo]
 * @property {boolean | null} [isLive]
 * @property {boolean | null} [showWatchButton]
 * @property {React.ElementType} [as]
 * @property {boolean} [disableLinks]
 * @property {boolean} [expandable]
 * @property {string} [linkName]
 * @property {boolean} [showActions]
 * @property {boolean} [showScheduleMetadata]
 */

/** @param {ScheduleCardProps} props */
export default function ScheduleCard({
  data,
  calendarDate,
  linkPage = undefined,
  dataForVideo,
  isLive,
  showWatchButton,
  as: RootComponent = 'li',
  disableLinks = false,
  expandable,
  linkName: linkNameOverride,
  showActions = true,
  showScheduleMetadata = true,
}) {
  const atWidth = useContainerWidth()
  const { [FLAGS.SITE_EXPERIENCE]: siteExperienceFlag } = useFlagsContext()
  const { treatment: siteTreatment } = siteExperienceFlag

  const {
    classroomName,
    conferenceYear,
    contentId,
    date,
    title,
    description,
    creators,
    exhibitors,
    url,
    promo,
    replay,
    sessionDifficulty,
    sessionFormat,
    sessionLocation,
  } = data

  const to = getRelativePath(url)
  const linksDisabled = disableLinks || siteTreatment === 'registration'
  const isExpandable = expandable ?? ['registration', 'countdown'].includes(siteTreatment)
  const asIconButton = atWidth({ default: true, xl: false })
  const linkName = linkNameOverride ?? `Watch: ${title}: Title`

  const creatorsWithoutLinks = creators?.map((creator) => ({ ...creator, url: undefined }))
  const exhibitorsWithoutLinks = exhibitors?.map((exhibitor) => ({ ...exhibitor, url: undefined }))

  const isInPersonOnly = sessionLocation === 'In Person'
  const { getNow } = useTimeTravel()
  const isPastBroadcast = getIsPastBroadcast({
    date,
    videoDuration: data?.video?.videoDuration,
    nowTimestamp: getNow(),
  })

  return (
    <RootComponent
      style={{ listStyle: 'none', paddingInlineStart: '0px', marginInlineStart: '0px' }}
      data-schedule-item={showScheduleMetadata && contentId ? `${contentId}:${calendarDate}` : undefined}
    >
      <CardListItem
        layout="compact"
        expandable={isExpandable}
        badges={
          <Badges
            location={sessionLocation || promo?.sessionLocation}
            difficulty={sessionDifficulty || promo?.sessionDifficulty}
            isLive={isLive}
            replay={showScheduleMetadata && replay && !isPastBroadcast}
          />
        }
        timestamp={
          <Timestamp
            conferenceYear={conferenceYear}
            timestamp={calendarDate ?? date}
            isLive={showScheduleMetadata && !isPastBroadcast}
          />
        }
        classroom={
          showScheduleMetadata && <Classroom date={date} sessionLocation={sessionLocation} classroom={classroomName} />
        }
        subHeader={
          <div css={scheduleMetadataCss}>
            <Paragraph as="div" size="sm" secondary>
              <Timestamp
                conferenceYear={conferenceYear}
                timestamp={calendarDate ?? date}
                isLive={showScheduleMetadata && !isPastBroadcast}
                inline
              />
              {showScheduleMetadata && (
                <Classroom date={date} sessionLocation={sessionLocation} classroom={classroomName} inline />
              )}
            </Paragraph>
          </div>
        }
        heading={title}
        linkName={linkName}
        description={description}
        creators={linksDisabled ? creatorsWithoutLinks : creators}
        exhibitors={linksDisabled ? exhibitorsWithoutLinks : exhibitors}
        to={linksDisabled ? undefined : to}
        dataForVideo={dataForVideo}
        additionalContent={
          showScheduleMetadata && siteTreatment === 'countdown' && isInPersonOnly ? (
            <>
              <Separator size="xxs" />
              <InPersonOnlyBadge />
              <Separator size="xxs" />
            </>
          ) : null
        }
        actions={
          showActions &&
          sessionFormat !== 'Workshop' &&
          (siteTreatment === 'countdown' || siteTreatment === 'conference') && (
            <Actions
              to={to}
              data={data}
              calendarDate={calendarDate}
              linkPage={linkPage}
              asIconButton={asIconButton}
              showWatchButton={showWatchButton}
            />
          )
        }
      />
    </RootComponent>
  )
}

// function parsePayLink(description) {
//   const el = document.createElement('html')
//   el.innerHTML = description
//   // look in htmlContent for the anchor tag with the pay link
//   const anchorList = el.querySelectorAll('a')
//   if (anchorList.length > 1) {
//     console.err('More than one anchor link found in description - unable to determine pay link')
//     return null
//   }
//   // return the href value
//   return anchorList[0]?.href || null
// }
